import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsInfoCircle, BsStars } from 'react-icons/bs';

import { ToneOfVoiceSelector } from '#emailCampaignFeature/components/advanced/ToneOfVoice';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';
import Switch from '#globalComponents/Forms/Switch';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';

const CreativeModeToggle = ({ enabled, readOnly, onChange }) => (
	<div>
		<div className="flex items-center justify-between p-4 bg-bgBase-300 rounded-card">
			<div className="flex-1 mr-4">
				<h4 className="text-sm font-medium text-text">
					Eigene Anweisungen exakt befolgen (nur für erfahrene Nutzer)
				</h4>
				<p className="text-sm text-bgBase-800 mt-1">
					Wenn aktiviert, folgt die KI Ihrer Beschreibung wortgetreu und nutzt keine Website-Inhalte als
					Aufhänger. Ideal, wenn Sie eine feste E-Mail-Vorlage vorgeben möchten.
				</p>
			</div>
			<Switch checked={enabled} onChange={() => onChange(!enabled)} disabled={readOnly} />
		</div>
		{enabled && (
			<div className="mt-2 flex items-start gap-2 p-3 bg-primary-100 rounded-card">
				<BsInfoCircle className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
				<p className="text-sm text-primary-700">
					Im exakten Modus wird Ihre Beschreibung als vollständige Anweisung behandelt. Schreiben Sie dort
					genau, wie die E-Mail aufgebaut sein soll (Struktur, Textbausteine, Regeln). Variablen bitte mit
					[Variablenname] kennzeichen.
					<br />
					Verfügbare Daten: Firmenname, Branche, Straße, Stadt, Mitarbeiterzahl, Telefonnummer, Vor, Nachname
					des Kontakts, Anrede, Position, Hauptvorteile, Call-to-Action Text, CTA-Link.
				</p>
			</div>
		)}
	</div>
);

CreativeModeToggle.propTypes = {
	enabled: PropTypes.bool.isRequired,
	readOnly: PropTypes.bool.isRequired,
	onChange: PropTypes.func.isRequired,
};

const ProductInfoForm = ({ initialData = {}, onSave, isSaving = false, readOnly = false }) => {
	const [formData, setFormData] = useState({
		productDescription: initialData.productDescription || '',
		targetAudience: initialData.targetAudience || '',
		keyBenefits: initialData.keyBenefits || '',
		callToAction: initialData.callToAction || '',
		ctaLink: initialData.ctaLink || '',
		disableCreativeMode: initialData.disableCreativeMode || false,
		language: initialData.language || 'Deutsch',
	});

	const [selectedToneId, setSelectedToneId] = useState(initialData.toneOfVoiceId || null);
	const [errors, setErrors] = useState({});

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: null }));
		}
	};

	const validate = () => {
		const newErrors = {};

		if (!formData.productDescription.trim()) {
			newErrors.productDescription = 'Beschreibung ist erforderlich';
		} else if (formData.productDescription.trim().length < 30) {
			newErrors.productDescription = 'Beschreibung sollte mindestens 30 Zeichen lang sein für bessere Ergebnisse';
		}

		if (!formData.callToAction.trim()) {
			newErrors.callToAction = 'Call-to-Action ist erforderlich';
		}

		// Validate URL if provided
		if (formData.ctaLink.trim()) {
			try {
				new URL(formData.ctaLink);
			} catch {
				newErrors.ctaLink = 'Bitte geben Sie eine gültige URL ein (z.B. https://example.com)';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (!validate()) {
			return;
		}

		onSave({
			...formData,
			toneOfVoiceId: selectedToneId,
			disableCreativeMode: formData.disableCreativeMode,
			language: formData.language,
		});
	};

	const isFormValid = formData.productDescription.trim().length >= 30 && formData.callToAction.trim();

	return (
		<Card>
			<form onSubmit={handleSubmit} className="space-y-5">
				{/* Disable Creative Mode Toggle */}
				<CreativeModeToggle
					enabled={formData.disableCreativeMode}
					readOnly={readOnly}
					onChange={(val) => handleChange('disableCreativeMode', val)}
				/>

				{/* Product/Service Description - label and placeholder adapt to mode */}
				<div>
					<label htmlFor="productDescription" className="block text-sm font-medium text-text mb-2">
						{formData.disableCreativeMode ? (
							<>
								Ihre Anweisungen für die E-Mail <span className="text-warning-500">*</span>
							</>
						) : (
							<>
								Was bieten Sie an? <span className="text-warning-500">*</span>
							</>
						)}
					</label>
					<Textarea
						id="productDescription"
						value={formData.productDescription}
						onChange={(e) => handleChange('productDescription', e.target.value)}
						disabled={readOnly}
						rows={formData.disableCreativeMode ? 10 : 4}
						error={Boolean(errors.productDescription)}
						placeholder={
							formData.disableCreativeMode
								? 'Beschreiben Sie hier exakt, wie die E-Mail aufgebaut sein soll. z.B.:\n\nSCHRITT 1 - EINSTIEG\nDer erste Satz beginnt immer mit...\n\nSCHRITT 2 - MAILTEXT\nNach dem Einstieg folgt dieser Text: ...\n\nSCHRITT 3 - BETREFF\nDer Betreff muss...'
								: "Beschreiben Sie Ihr Produkt oder Ihre Dienstleistung. z.B. 'Wir sind eine Webdesign-Agentur, die sich auf moderne, conversion-optimierte Websites für B2B-Unternehmen spezialisiert hat.'"
						}
					/>
					<div className="flex justify-between items-center mt-1">
						{errors.productDescription ? (
							<p className="text-sm text-warning-500">{errors.productDescription}</p>
						) : (
							<p className="text-sm text-bgBase-800">
								{formData.productDescription.length} Zeichen (mind. 30 für gute Ergebnisse)
							</p>
						)}
					</div>
				</div>

				{/* Key Benefits */}
				<div>
					<label htmlFor="keyBenefits" className="block text-sm font-medium text-text mb-2">
						Wichtigste Vorteile <span className="text-bgBase-800">(optional, aber empfohlen)</span>
					</label>
					<Textarea
						id="keyBenefits"
						value={formData.keyBenefits}
						onChange={(e) => handleChange('keyBenefits', e.target.value)}
						disabled={readOnly}
						rows={3}
						placeholder="• Schnellere Umsetzung in 4 Wochen statt 3 Monaten&#10;• 30% höhere Conversion Rate&#10;• Persönlicher Ansprechpartner während des gesamten Projekts"
					/>
				</div>

				{/* Target Audience - hidden in directive mode */}
				{!formData.disableCreativeMode && (
					<div>
						<label htmlFor="targetAudience" className="block text-sm font-medium text-text mb-2">
							Wer ist die Zielgruppe? <span className="text-bgBase-800">(optional)</span>
						</label>
						<TextInput
							id="targetAudience"
							value={formData.targetAudience}
							onChange={(e) => handleChange('targetAudience', e.target.value)}
							disabled={readOnly}
							placeholder="z.B. B2B-Unternehmen mit 10-200 Mitarbeitern, Geschäftsführer oder Marketing-Leiter"
						/>
					</div>
				)}

				{/* Call to Action */}
				<div>
					<label htmlFor="callToAction" className="block text-sm font-medium text-text mb-2">
						Was soll der Empfänger tun (Call-to-Action)? <span className="text-warning-500">*</span>
					</label>
					<TextInput
						id="callToAction"
						value={formData.callToAction}
						onChange={(e) => handleChange('callToAction', e.target.value)}
						disabled={readOnly}
						error={Boolean(errors.callToAction)}
						placeholder="z.B. Vereinbaren Sie einen Beratungstermin / Fordern Sie ein unverbindliches Angebot an"
					/>
					{errors.callToAction && <p className="text-sm text-warning-500 mt-1">{errors.callToAction}</p>}
					{!formData.disableCreativeMode && (
						<p className="text-sm text-bgBase-800 mt-1">
							Die KI wird dies natürlich formulieren, nicht wörtlich übernehmen
						</p>
					)}
				</div>

				{/* CTA Link */}
				<div>
					<label htmlFor="ctaLink" className="block text-sm font-medium text-text mb-2">
						Link für den Call-to-Action <span className="text-bgBase-800">(optional)</span>
					</label>
					<TextInput
						id="ctaLink"
						type="url"
						value={formData.ctaLink}
						onChange={(e) => handleChange('ctaLink', e.target.value)}
						disabled={readOnly}
						error={Boolean(errors.ctaLink)}
						placeholder="https://example.com/termin-buchen"
					/>
					{errors.ctaLink && <p className="text-sm text-warning-500 mt-1">{errors.ctaLink}</p>}
					<p className="text-sm text-bgBase-800 mt-1">
						Dieser Link wird in den generierten E-Mails als klickbarer Link eingefügt
					</p>
				</div>

				{/* Language Selector */}
				<div>
					<label htmlFor="language" className="block text-sm font-medium text-text mb-2">
						Sprache der E-Mail
					</label>
					<Select
						id="language"
						value={formData.language}
						onChange={(e) => handleChange('language', e.target.value)}
						disabled={readOnly}
					>
						<option value="Deutsch">Deutsch</option>
						<option value="English">English</option>
					</Select>
					<p className="text-sm text-bgBase-800 mt-1">
						Die generierte E-Mail wird in dieser Sprache verfasst
					</p>
				</div>

				{/* Tone of Voice Selector */}
				<div className="pt-4 border-t">
					<ToneOfVoiceSelector
						selectedToneId={selectedToneId}
						onSelect={setSelectedToneId}
						disabled={readOnly || isSaving}
					/>
				</div>

				{/* Submit Button */}
				{!readOnly && (
					<div className="flex justify-end pt-4 border-t">
						<Button type="submit" disabled={isSaving || !isFormValid}>
							<BsStars className="h-5 w-5" />
							{isSaving ? 'Generiere E-Mails...' : 'E-Mails generieren'}
						</Button>
					</div>
				)}
			</form>
		</Card>
	);
};

ProductInfoForm.propTypes = {
	initialData: PropTypes.shape({
		productDescription: PropTypes.string,
		targetAudience: PropTypes.string,
		keyBenefits: PropTypes.string,
		callToAction: PropTypes.string,
		ctaLink: PropTypes.string,
		toneOfVoiceId: PropTypes.string,
		disableCreativeMode: PropTypes.bool,
		language: PropTypes.string,
	}),
	onSave: PropTypes.func.isRequired,
	isSaving: PropTypes.bool,
	readOnly: PropTypes.bool,
};

export default ProductInfoForm;
